import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, TextField, Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SendIcon from '@mui/icons-material/Send';
import ReplayIcon from '@mui/icons-material/Replay';

/**
 * Reservation actions of the webshop admin (2026-10-05), used by the Data tab
 * (per row) and the Actions screen (by number):
 *  - put a stuck reservation back in the queue (mini-api POST
 *    /reservations/{id}/requeue; the cron process_queued_reservations sends it
 *    to the POS), optionally running that cron right away;
 *  - send the order overview mail again to the customer and/or the store
 *    (POST /reservations/resend-mail).
 * `proxy(method, path, body)` is the host screen's auth-proxy call.
 */

const tro = (t) => (k, fb) => (typeof t === 'function' ? t(k, fb) : fb);
const isOnlineUnpaid = (r) => String(r.payment_type || '').toUpperCase() === 'ONLINE'
    && Number(r.payed_online) !== 1 && String(r.payed_status || '').toUpperCase() !== 'AUTHORIZED';

/** Why a row cannot go back in the queue (same rules as the mini-api), or ''. */
export const requeueBlocker = (row, t) => {
    const tr = tro(t);
    if (!row) return '';
    if (Number(row.sent_reservation) === 1) return tr('cfg_res_requeue_block_sent', 'Already in the POS: requeueing would create it a second time.');
    if (Number(row.is_queued) === 1) return tr('cfg_res_requeue_block_queued', 'Already waiting in the queue.');
    if (isOnlineUnpaid(row)) return tr('cfg_res_requeue_block_unpaid', 'The online payment is not completed.');
    return '';
};

/** Whether the overview mail can be sent (the reservation has a POS number). */
export const canResendMail = (row) => !!row && Number(row.reservation_id) > 0;

/**
 * Run one of the shop's own scripts (e.g. the queue cron) in a hidden iframe,
 * like the Actions screen does: resolves true when the page has loaded.
 */
export const runShopScript = (url, timeoutMs = 120000) => new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    let done = false;
    const finish = (ok) => {
        if (done) return;
        done = true;
        setTimeout(() => iframe.remove(), 100);
        resolve(ok);
    };
    iframe.onload = () => finish(true);
    iframe.onerror = () => finish(false);
    setTimeout(() => finish(false), timeoutMs);
    iframe.src = url;
    document.body.appendChild(iframe);
});

const ResultLines = ({ results, tr }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }} data-res-results>
        {results.map((r, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                {r.sent ? <CheckCircleOutlineIcon color="success" sx={{ fontSize: 18, mt: '1px' }} /> : <ErrorOutlineIcon color="error" sx={{ fontSize: 18, mt: '1px' }} />}
                <Typography variant="body2">
                    <strong>{r.who === 'store' ? tr('cfg_res_mail_who_store', 'Store') : tr('cfg_res_mail_who_customer', 'Customer')}</strong>
                    {r.to ? ` · ${r.to}` : ''}
                    {' — '}{r.sent ? tr('cfg_res_mail_sent', 'sent') : (r.reason || tr('cfg_res_mail_not_sent', 'not sent'))}
                </Typography>
            </Box>
        ))}
    </Box>
);
ResultLines.propTypes = { results: PropTypes.array, tr: PropTypes.func };

/**
 * Send the overview mail again. With `row` (Data tab) the reservation is
 * known; without it (Actions screen) the user types the reservation number.
 */
export const ResendMailDialog = ({ open, onClose, t, proxy, row, onDone }) => {
    const tr = tro(t);
    const [number, setNumber] = useState('');
    const [toCustomer, setToCustomer] = useState(true);
    const [email, setEmail] = useState('');
    const [toStore, setToStore] = useState(false);
    const [withBcc, setWithBcc] = useState(true);
    const [sending, setSending] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setNumber('');
        setToCustomer(true);
        setEmail(row?.customer_email || '');
        setToStore(false);
        setWithBcc(true);
        setResults(null);
        setError('');
    }, [open, row]);

    const emailTrim = email.trim();
    const emailBad = emailTrim !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim);
    const canSend = !sending && (toCustomer || toStore) && !(toCustomer && emailBad) && (row ? true : number.trim() !== '');

    const send = async () => {
        setSending(true);
        setError('');
        setResults(null);
        // the customer's own address goes as "no override", so the server
        // also marks the reservation's mail as sent
        const own = (row?.customer_email || '').trim().toLowerCase();
        const body = {
            to_customer: toCustomer,
            to_store:    toStore,
            with_bcc:    withBcc,
            email:       toCustomer && emailTrim && emailTrim.toLowerCase() !== own ? emailTrim : '',
            ...(row ? { id: row.id } : { reservation_id: number.trim() }),
        };
        try {
            const r = await proxy('POST', '/reservations/resend-mail', body);
            if (r && r.success) {
                setResults(r.data?.results || []);
                if (onDone) onDone(r.data);
            } else {
                setError(r?.data?.body?.message || r?.message || tr('cfg_res_mail_failed', 'Sending failed.'));
            }
        } catch (e) {
            setError(e?.message || tr('cfg_res_mail_failed', 'Sending failed.'));
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }} data-res-mail-dialog>
            <DialogTitle>
                <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                    {tr('cfg_res_mail_title', 'Send order mail again')}
                    {row?.reservation_id ? ` · ${row.reservation_id}` : ''}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {tr('cfg_res_mail_desc', 'The order overview the customer got at checkout, sent again. "To the store" goes to that store\'s email address (Pages › About); the shop copy (BCC) to the BCC recipients in Webshop connections › SMTP credentials.')}
                    </Typography>
                    {!row && (
                        <TextField size="small" fullWidth autoFocus value={number} onChange={(e) => setNumber(e.target.value)}
                            label={tr('cfg_res_mail_number', 'Reservation number')} inputProps={{ 'data-res-number': true }} />
                    )}
                    <FormControlLabel control={<Checkbox checked={toCustomer} onChange={(e) => setToCustomer(e.target.checked)} />}
                        label={tr('cfg_res_mail_to_customer', 'To the customer')} />
                    {toCustomer && (
                        <Box sx={{ pl: 4, mt: -0.5 }}>
                            <TextField size="small" fullWidth type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                label={tr('cfg_res_mail_address', 'Address')} error={emailBad}
                                placeholder={row ? '' : tr('cfg_res_mail_address_ph', 'Empty = the address on the reservation')}
                                helperText={emailBad ? tr('cfg_res_mail_address_bad', 'Not a valid email address.') : tr('cfg_res_mail_address_help', 'Change it to send to another address (e.g. a typo in the order).')}
                                inputProps={{ 'data-res-email': true }} />
                        </Box>
                    )}
                    <FormControlLabel control={<Checkbox checked={toStore} onChange={(e) => setToStore(e.target.checked)} />}
                        label={tr('cfg_res_mail_to_store', 'To the store')} />
                    <FormControlLabel control={<Checkbox checked={withBcc} onChange={(e) => setWithBcc(e.target.checked)} />}
                        label={tr('cfg_res_mail_bcc', 'Shop copy (BCC addresses from the mail settings)')} />
                    {error && <Alert severity="error">{error}</Alert>}
                    {results && <ResultLines results={results} tr={tr} />}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={sending} sx={{ textTransform: 'none' }}>{results ? tr('cfg_act_close', 'Close') : tr('cfg_act_cancel', 'Cancel')}</Button>
                <Button variant="contained" onClick={send} disabled={!canSend} sx={{ textTransform: 'none' }}
                    startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}>
                    {results ? tr('cfg_res_mail_again', 'Send again') : tr('cfg_res_mail_send', 'Send')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
ResendMailDialog.propTypes = {
    open: PropTypes.bool, onClose: PropTypes.func, t: PropTypes.func, proxy: PropTypes.func,
    row: PropTypes.object, onDone: PropTypes.func,
};

/** Put a stuck reservation back in the queue, optionally processing the queue now. */
export const RequeueDialog = ({ open, onClose, t, proxy, row, shopBase, onDone }) => {
    const tr = tro(t);
    const [processNow, setProcessNow] = useState(true);
    const [busy, setBusy] = useState('');          // '' | 'queue' | 'process'
    const [done, setDone] = useState(null);        // { processed: bool|null }
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setProcessNow(true);
        setBusy('');
        setDone(null);
        setError('');
    }, [open, row]);

    const go = async () => {
        setError('');
        setBusy('queue');
        try {
            const r = await proxy('POST', `/reservations/${row.id}/requeue`);
            if (!r || !r.success) {
                setError(r?.data?.body?.message || r?.message || tr('cfg_res_requeue_failed', 'Could not put it back in the queue.'));
                setBusy('');
                return;
            }
            let processed = null;
            if (processNow && shopBase) {
                setBusy('process');
                processed = await runShopScript(`${shopBase}/server/v1/crons/process_queued_reservations.php`);
            }
            setDone({ processed });
            if (onDone) onDone();
        } catch (e) {
            setError(e?.message || tr('cfg_res_requeue_failed', 'Could not put it back in the queue.'));
        }
        setBusy('');
    };

    const who = row ? [row.customer_firstname, row.customer_lastname].filter(Boolean).join(' ') : '';
    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }} data-res-requeue-dialog>
            <DialogTitle>
                <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                    {tr('cfg_res_requeue_title', 'Back in the queue')}{row ? ` · #${row.id}` : ''}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {row && (
                        <Typography variant="body2">
                            {[who, row.customer_email, row.total_amount != null ? `€${Number(row.total_amount).toFixed(2)}` : '', row.created_at].filter(Boolean).join(' · ')}
                        </Typography>
                    )}
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {tr('cfg_res_requeue_desc', 'The reservation is processed again as at checkout: sent to the POS, the print message to the store and the confirmation mail to the customer. Only for a reservation that did not reach the POS (e.g. after fixing a missing article).')}
                    </Typography>
                    {!done && (
                        <FormControlLabel control={<Checkbox checked={processNow} onChange={(e) => setProcessNow(e.target.checked)} disabled={!!busy} />}
                            label={tr('cfg_res_requeue_now', 'Process the queue right away (otherwise at the next automatic run, within ~10 minutes)')} />
                    )}
                    {busy === 'process' && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><CircularProgress size={16} />
                            <Typography variant="body2">{tr('cfg_res_requeue_processing', 'Processing the queue…')}</Typography></Box>
                    )}
                    {error && <Alert severity="error">{error}</Alert>}
                    {done && (
                        <Alert severity={done.processed === false ? 'warning' : 'success'} data-res-requeue-done>
                            {done.processed === null
                                ? tr('cfg_res_requeue_done_later', 'Back in the queue. It is processed at the next automatic run.')
                                : done.processed
                                    ? tr('cfg_res_requeue_done_now', 'Back in the queue and the queue was processed. Check the columns "Sent" and "Email sent" in the list.')
                                    : tr('cfg_res_requeue_done_noanswer', 'Back in the queue, but the queue run gave no answer in time. It is processed at the latest at the next automatic run.')}
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={!!busy} sx={{ textTransform: 'none' }}>{done ? tr('cfg_act_close', 'Close') : tr('cfg_act_cancel', 'Cancel')}</Button>
                {!done && (
                    <Button variant="contained" onClick={go} disabled={!!busy || !row} sx={{ textTransform: 'none' }}
                        startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}>
                        {tr('cfg_res_requeue_btn', 'Put back in the queue')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
RequeueDialog.propTypes = {
    open: PropTypes.bool, onClose: PropTypes.func, t: PropTypes.func, proxy: PropTypes.func,
    row: PropTypes.object, shopBase: PropTypes.string, onDone: PropTypes.func,
};
